import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react';
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  showFirstLast?: boolean;
}
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true
}: PaginationProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    // Update URL with new page
    const newSearchParams = new URLSearchParams(searchParams);
    if (page === 1) {
      newSearchParams.delete('page');
    } else {
      newSearchParams.set('page', page.toString());
    }
    const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`;
    navigate(newUrl);
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // Call optional callback
    if (onPageChange) {
      onPageChange(page);
    }
  };
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Maximum number of page buttons to show
    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      if (currentPage > 3) {
        pages.push('...');
      }
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      // Always show last page
      pages.push(totalPages);
    }
    return pages;
  };
  if (totalPages <= 1) return null;
  return <div className="flex items-center justify-center gap-2 py-8" data-id="element-5514" data-mp="3f5sf3zzu38ve">
      {/* First Page Button */}
      {showFirstLast && <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="First page" data-id="element-5515" data-mp="3a8a13v6ym3gj">
          <ChevronsLeftIcon className="w-5 h-5 text-gray-600" data-id="element-5516" data-mp="1wvzkxhyhvqr" />
        </button>}
      {/* Previous Button */}
      <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="Previous page" data-id="element-5517" data-mp="3t376ha9ndqq0">
        <ChevronLeftIcon className="w-5 h-5 text-gray-600" data-id="element-5518" data-mp="kmvmbdqbqyr0" />
      </button>
      {/* Page Numbers */}
      <div className="flex items-center gap-2" data-id="element-5519" data-mp="foamvgiwcj3m">
        {getPageNumbers().map((page, index) => {
        if (page === '...') {
          return <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500" data-id="element-5520" data-mp="149p49b51ogq9">
                ...
              </span>;
        }
        const pageNum = page as number;
        const isActive = pageNum === currentPage;
        return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`min-w-[40px] px-3 py-2 rounded-lg font-medium transition-all ${isActive ? 'bg-[#1E2B55] text-white shadow-md' : 'border border-gray-300 hover:bg-gray-50 text-gray-700'}`} aria-label={`Page ${pageNum}`} aria-current={isActive ? 'page' : undefined} data-id="element-5521" data-mp="gjuretkq6s60" data-mpi={index}>
              {pageNum}
            </button>;
      })}
      </div>
      {/* Next Button */}
      <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="Next page" data-id="element-5522" data-mp="2nr0b955ezxr">
        <ChevronRightIcon className="w-5 h-5 text-gray-600" data-id="element-5523" data-mp="3gygl9khhg1at" />
      </button>
      {/* Last Page Button */}
      {showFirstLast && <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="Last page" data-id="element-5524" data-mp="37zlb5xbbi728">
          <ChevronsRightIcon className="w-5 h-5 text-gray-600" data-id="element-5525" data-mp="2u0logv7d828p" />
        </button>}
    </div>;
}